import React, { useState, useRef, useEffect, useMemo } from "react";
import { User, Check } from "lucide-react";

/**
 * Customer name input with autocomplete from an existing customers list.
 *
 * - Behaves like a normal text input (free text always allowed)
 * - When the user types, a dropdown shows up to 6 matching customers
 * - Picking a customer fires `onPickCustomer(customer)` so the parent can
 *   auto-fill related fields (address, phone, email, etc.)
 *
 * Props:
 *   value            : current text value (string)
 *   onChange(str)    : called when user types or picks a customer (string)
 *   onPickCustomer(c): called only when user picks an existing customer
 *   customers        : array of customer objects (must include `name`)
 *   placeholder      : input placeholder
 *   className        : optional extra class
 *   inputProps       : passed through to the inner <Input>
 */
export const CustomerAutocompleteInput = ({
  value = "",
  onChange,
  onPickCustomer,
  customers = [],
  placeholder = "John Smith",
  className = "",
  inputProps = {},
  testIdPrefix = "customer-autocomplete",
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [open]);

  const suggestions = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    if (!q || q.length < 1) return [];
    const matches = (customers || []).filter((c) => {
      const name = (c.name || "").toLowerCase();
      const email = (c.email || "").toLowerCase();
      const addr = (c.address || "").toLowerCase();
      return name.includes(q) || email.includes(q) || addr.includes(q);
    });
    // De-dupe by email (the customers list is already aggregated by email)
    const seen = new Set();
    const out = [];
    for (const c of matches) {
      const key = (c.email || c.name || "").toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(c);
      if (out.length >= 6) break;
    }
    return out;
  }, [customers, value]);

  // Suggestion is "useful" only when the current value isn't already an exact match
  const exactMatch = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    return customers.find((c) => (c.name || "").toLowerCase() === q);
  }, [customers, value]);
  const shouldShow = open && suggestions.length > 0 && !exactMatch;

  const handlePick = (c) => {
    onChange(c.name || "");
    onPickCustomer && onPickCustomer(c);
    setOpen(false);
    // blur so the dropdown closes neatly on mobile
    if (inputRef.current) inputRef.current.blur();
  };

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
        data-testid={`${testIdPrefix}-input`}
        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        {...inputProps}
      />

      {shouldShow && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden"
          data-testid={`${testIdPrefix}-dropdown`}
        >
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide font-medium text-slate-400 bg-slate-50 border-b border-slate-100">
            Existing customers
          </div>
          <ul className="max-h-56 overflow-y-auto">
            {suggestions.map((c) => (
              <li key={c.email || c.name}>
                <button
                  type="button"
                  onClick={() => handlePick(c)}
                  data-testid={`${testIdPrefix}-suggestion`}
                  className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-blue-50 transition-colors"
                >
                  <User className="w-3.5 h-3.5 text-blue-800 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">
                      {c.name || "(unnamed)"}
                      {c.is_demo && (
                        <span className="text-[9px] uppercase font-medium bg-amber-100 text-amber-700 rounded px-1 py-0.5">
                          demo
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {c.address ? c.address : c.email || c.phone || "no address on file"}
                    </div>
                  </div>
                  <Check className="w-3.5 h-3.5 text-transparent group-hover:text-blue-700 flex-shrink-0 mt-0.5" />
                </button>
              </li>
            ))}
          </ul>
          <div className="px-3 py-1.5 text-[10px] text-slate-400 bg-slate-50 border-t border-slate-100">
            Or keep typing to create a new customer
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerAutocompleteInput;
